import { api } from './api.js';

export type PushState =
  | 'unsupported' // browser cannot do web push at all
  | 'needs-install' // iPhone: must be added to the Home Screen first
  | 'denied' // the person said no in the browser prompt
  | 'off'
  | 'on';

export const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
export const isIos = () => /iPhone|iPad|iPod/.test(navigator.userAgent) && !(window as { MSStream?: unknown }).MSStream;
export const isAndroid = () => /Android/.test(navigator.userAgent);

const urlBase64ToUint8Array = (s: string) => {
  const padding = '='.repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export async function pushState(): Promise<PushState> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  }
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? 'on' : 'off';
}

/** Asks for permission, subscribes this browser and registers it with the API. */
export async function enablePush(): Promise<PushState> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';
  const reg = await navigator.serviceWorker.ready;
  const { publicKey } = await api.call<{ publicKey: string }>('GET', '/driver/push');
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }));
  await api.call('POST', '/driver/push/subscribe', { body: sub.toJSON() });
  return 'on';
}

export async function disablePush(): Promise<PushState> {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api.call('DELETE', '/driver/push/subscribe', { body: { endpoint: sub.endpoint } }).catch(() => undefined);
    await sub.unsubscribe();
  }
  return 'off';
}

export async function testPush(): Promise<number> {
  const r = await api.call<{ delivered: number }>('POST', '/driver/push/test');
  return r.delivered;
}

/** Re-registers a rotated subscription that the service worker reports. */
export function listenForResubscribe() {
  navigator.serviceWorker?.addEventListener('message', (e) => {
    const m = e.data as { type?: string; subscription?: unknown };
    if (m?.type === 'resubscribe' && m.subscription) void api.call('POST', '/driver/push/subscribe', { body: m.subscription });
  });
}
